// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
import { PLANTING_STATUS, formatTime } from '@seedplanner/shared-utils';
import * as React from 'react';
import { Box, Checkbox, Collapse, FormControlLabel, IconButton, Typography } from '../../lib/base';
import { KeyboardArrowDownIcon, KeyboardArrowUpIcon } from '../../lib/icons';
import { getID, getNumericID } from '../../utils';
import { PlantingListContainer } from '../order/PlantingListContainer';

interface PlantingsData {
  readonly id: string;
  readonly planting_status: string;
  readonly trays_rounded: string;
  readonly yield_per_tray_ounces: string;
  readonly crop?: {
    readonly id: string;
    readonly crop_name: string;
    readonly yield_unit: string;
  } | null;
}

type OrderContainerData = {
  id: string;
  order_fulfillment_time: number;
  order_status: string;
  plantings: {
    edges: {
      node: PlantingsData;
    }[];
  };
};

export default function CancellableOrdersList({
  orders,
  timezone,
  selectedOrders,
  setSelectedOrders,
}: {
  readonly orders: OrderContainerData[];
  readonly timezone: string;
  readonly selectedOrders: number[];
  readonly setSelectedOrders: (orders: number[]) => void;
}) {
  const ordersWithFormats = orders.map((order) => ({
    ...order,
    formattedTime: formatTime(order.order_fulfillment_time, { timezone }),
    formattedId: getID(order.id),
  }));
  const maxIdLength = Math.max(...ordersWithFormats.map((order) => order.formattedId.length));
  const maxTimeLength = Math.max(...ordersWithFormats.map((order) => order.formattedTime.length));

  const ordersWithLabels = ordersWithFormats.map((order) => ({
    ...order,
    label: `Order ${order.formattedId.padEnd(maxIdLength, ' ')} - ${order.formattedTime.padEnd(maxTimeLength, ' ')}`,
  }));

  return (
    <>
      <Typography>Select which of the following in-progress child orders to cancel:</Typography>
      <ul>
        {ordersWithLabels.map((order) => {
          return (
            <SelectCancellableOrder
              isSelected={selectedOrders.includes(getNumericID(order.id))}
              onSelectOrder={(isSelected) => {
                const updatedSelectedOrders = isSelected
                  ? [...selectedOrders, getNumericID(order.id)]
                  : selectedOrders.filter((id) => id !== getNumericID(order.id));
                setSelectedOrders(updatedSelectedOrders);
              }}
              key={order.id}
              order={order}
              label={order.label}
            />
          );
        })}
      </ul>
    </>
  );
}

function SelectCancellableOrder({
  order,
  label,
  isSelected,
  onSelectOrder,
}: {
  readonly order: OrderContainerData;
  readonly label: string;
  readonly isSelected: boolean;
  readonly onSelectOrder: (isSelected: boolean) => void;
}) {
  const [open, setOpen] = React.useState(false);
  const onChangeSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    onSelectOrder(e.target.checked);
    if (!e.target.checked) {
      setOpen(false);
    }
  };
  // Display the order information plus a checkbox to select orders to cancel
  // When an order is selected, add it to a list of orders to cancel
  // When selected, show a dropdown to display plantings to be moved to surplus and plantings to be deleted
  const plantings = order.plantings.edges.map((n) => n.node);
  const inProgressOrCompletedPlantings = plantings.filter((n) => n.planting_status !== PLANTING_STATUS.PLANNED);
  const plannedPlantings = plantings.filter((n) => n.planting_status === PLANTING_STATUS.PLANNED);

  const hasPlannedPlantings = plannedPlantings.length > 0;
  const hasInProgressOrCompletedPlantings = inProgressOrCompletedPlantings.length > 0;

  return (
    <li key={order.id}>
      <Box display="flex" flexDirection="row" alignItems="center">
        <FormControlLabel
          sx={{ margin: 0, '& .MuiFormControlLabel-label': { flex: 1 } }}
          label={label}
          control={<Checkbox checked={isSelected} onChange={onChangeSelected} />}
          labelPlacement="start"
        />
        <IconButton aria-label="show-details" sx={{ ml: 1 }} onClick={() => setOpen(!open)}>
          {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
        </IconButton>
      </Box>
      <Collapse
        in={open}
        unmountOnExit
        sx={{
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {hasPlannedPlantings && (
          <Box sx={{ pb: 1 }}>
            <PlantingListContainer
              addPadding={true}
              plantingsIn={plannedPlantings}
              title="These plantings will be deleted:"
              showAmount={false}
            />
          </Box>
        )}
        {hasInProgressOrCompletedPlantings && (
          <Box sx={{ pb: 1 }}>
            <PlantingListContainer
              addPadding={true}
              plantingsIn={inProgressOrCompletedPlantings}
              title="These plantings will be added to your surpluses:"
              showAmount={true}
            />
          </Box>
        )}
      </Collapse>
    </li>
  );
}
